import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StorageService } from '../core/services/storage.service';
import { isNumeric } from '../core/utils/common-functions';
import { UserProfile } from '../models/common/user-profile.model';
import { Configuration } from './configuration';

// env.json file from library
const ENV_CONFIG_URL = 'assets/env/env.json?v=' + Date.now();

/**
 * @description 載入 env.json 初始化 AppConfig
 *
 * @param appConfig AppConfig 物件
 * @return function
 */
export function initAppConfig(appConfig: AppConfig): () => {} {
  return () => appConfig.load();
}

@Injectable()
export class AppConfig {
  private env: any = {};
  private defaultCurrencyCode = '';

  constructor(
    private configuration: Configuration,
    private httpBackend: HttpBackend,
    private storageService: StorageService
  ) {}

  /**
   * @description 取得國家代碼
   *
   * @return country id
   */
  get countryId(): string {
    return this.env.countryId || '';
  }

  /**
   * @description 取得格式化設定(日期)
   *
   * datePrimeng: primeng calendar dateFormat setting
   *
   * datePrimengPlaceholder: primeng calendar placeholder setting
   *
   * datePrimengInputmask: primeng calendar inputmask setting
   *
   * @return date format object
   */
  get format(): {
    date: string;
    datetime: string;
    dateYM: string;
    datePrimeng: string;
    datePrimengPlaceholder: string;
    datePrimengInputmask: string;
  } {
    return {
      date: this.env.format?.date || '',
      datetime: this.env.format?.datetime || '',
      dateYM: this.env.format?.dateYM || '',
      datePrimeng: this.env.format?.datePrimeng || '',
      datePrimengPlaceholder: this.env.format?.datePrimengPlaceholder || '',
      datePrimengInputmask: this.env.format?.datePrimengInputmask || '',
    };
  }

  /**
   * @description 取得 recaptcha site key
   *
   * @return recaptcha site key
   */
  get recaptchaSiteKey(): string {
    return this.env.recaptchaSiteKey || '';
  }

  /**
   * @description 取得 env 設定
   *
   * @return json object
   */
  public getEnv(): any {
    return this.env;
  }

  /**
   * @description 依據 channel 取得 api url(進件外網 Sales 登入時從 env apiSalesUrl 取得 api url)
   *
   * @param channel channel(現有 channel: rbac, fileCenter, pendingItem, webSubmission, creditRatingScales, writeOff)
   * @return api url
   */
  public getApiUrl(channel: string): string {
    if (this.isWebSubmissionExtranetSalesLogin() && this.env.apiSalesUrl) {
      return this.env.apiSalesUrl[channel] || '';
    }

    if (this.env.apiUrl) {
      return this.env.apiUrl[channel] || '';
    }

    return '';
  }

  /**
   * @description 依據幣別代碼取得數字格式設定(現有幣別代碼 USD, KHR, PHP, MYR, IDR)
   *
   * @param currencyCode 幣別代碼
   * @return number format
   */
  public getNumberFormatByCurrencyCode(currencyCode: string): string {
    if (currencyCode && this.env.format && this.env.format.currency) {
      return this.env.format.currency[currencyCode.toUpperCase()] || '';
    }
    return '';
  }

  /**
   * @description 依據幣別代碼取得小數位數長度設定，若找不到設定固定回傳 0
   * @param currencyCode 幣別代碼
   * @returns 小數位數長度
   */
  public getDecimalLengthByCurrencyCode(currencyCode: string): number {
    if (currencyCode && this.env.format && this.env.format.currency) {
      const setting = this.env.format.currency[
        currencyCode.toUpperCase()
      ] as string;
      const formatRegex = /^\d+\.\d+\-\d+$/g;
      if (setting && formatRegex.test(setting)) {
        const maxFractionDigits = setting.split('-')[1];

        if (isNumeric(maxFractionDigits)) {
          return parseInt(maxFractionDigits, 10);
        }
      }
    }
    return 0;
  }

  /**
   * @description 取得 azureAD 設定
   *
   * @returns env azureAD 設定
   */
  public getAzureAD(): {
    enabled: boolean;
    auth: {
      clientId: string;
      authority: string;
      redirectUri: string;
      postLogoutRedirectUri: string;
    };
    resourceMap: {
      uri: string;
      scopes: string[];
    };
  } {
    return {
      enabled: this.env.azureAD?.enabled,
      auth: {
        clientId: this.env.azureAD?.auth?.clientId || '',
        authority: this.env.azureAD?.auth?.authority || '',
        redirectUri: this.env.azureAD?.auth?.redirectUri || '',
        postLogoutRedirectUri:
          this.env.azureAD?.auth?.postLogoutRedirectUri || '',
      },
      resourceMap: {
        uri: this.env.azureAD?.resourceMap?.uri || '',
        scopes: this.env.azureAD?.resourceMap?.scopes || [],
      },
    };
  }

  /**
   * @description Loads "env.json"
   */
  public load(): Promise<void> {
    return new Promise((resolve, reject) =>
      new HttpClient(this.httpBackend)
        .get<any>(ENV_CONFIG_URL)
        .pipe(
          catchError(() => {
            reject('Failed to load env.json.');
            return EMPTY;
          })
        )
        .subscribe((response) => {
          this.env = response;
          this.defaultCurrencyCode = this.setDefaultCurrencyCode();
          resolve();
        })
    );
  }

  /**
   * @description 檢查是否為進件外網 Sales 登入
   *
   * @return 是否為進件外網 Sales 登入
   */
  private isWebSubmissionExtranetSalesLogin(): boolean {
    const userProfile: UserProfile | undefined =
      this.storageService.local.getItem(this.storageService.keys.UserProfile) ||
      this.storageService.session.getItem(this.storageService.keys.UserProfile);

    return (
      this.configuration.systemGroupCode === 'WebSubmission' &&
      this.configuration.isExtranet &&
      userProfile?.extranetUserRole === 0
    );
  }

  public getDefaultCurrencyCode(): string {
    return this.defaultCurrencyCode;
  }

  private setDefaultCurrencyCode(): string {
    if (this.env.format.currency) {
      return (
        Object.keys(this.env.format.currency).find(
          (value) => value.slice(0, 2) === this.countryId
        ) || ''
      );
    }
    return '';
  }
}
